import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Show } from "shared/components";
import { Applicant } from "types/application";
import { getErrorMessage } from "utils/error";
import { getPresignedUrl, uploadFileToS3Bucket } from "utils/fileHandlers";

import { IntroView } from "./slices/IntroView";
import { getApplicantRole } from "utils/getApplicantRole";
import { getFullName } from "shared/utils/get-full-name";
import { CombinedIncome } from "../../types/income";
import { VerificationSettings } from "types/landlord/reports";
import { IncomeType } from "shared/types";

import { DocType } from "./types";
import { DocTypeView } from "./slices/DocTypeView";
import { DropZoneView } from "./slices/DropZoneView";
import { ConfirmView } from "./slices/ConfirmView";
import { getIncomeJobName } from "../../utils";
import { getFileUploadErrorMessage, normalizeFilename } from "shared/utils";

enum Views {
  Intro,
  DocType,
  UploadDoc,
  Confirm
}

const JOURNEY = [Views.Intro, Views.DocType, Views.UploadDoc, Views.Confirm];

// Offer exactly the document types the verification template enables for this income type — the
// same source of truth AX uses — so a government-benefits income can be re-verified with a benefit
// letter instead of being forced into a paystub or W2 (BSCPF-6301).
const getAllowedDocTypes = (
  settings?: VerificationSettings,
  incomeType?: IncomeType
): DocType[] => {
  const config = incomeType ? settings?.[incomeType] : undefined;
  const allowed = [
    DocType.Paystub,
    DocType.W2,
    DocType.BenefitsStatement
  ].filter((docType) => config?.[docType]?.allowed);

  // Without settings we cannot tell what is enabled; keep the historic pay stub / W2 pair.
  return allowed.length ? allowed : [DocType.Paystub, DocType.W2];
};

export const ReverifyWithDocumentsFlow = ({
  opened,
  onClose,
  onBack,
  applicant,
  applicationId,
  verificationId,
  incomeId,
  income,
  onSuccess,
  settings
}: {
  opened: boolean;
  onClose: () => void;
  onBack: () => void;
  applicationId: number | string;
  applicant: Applicant;
  verificationId: number | string;
  incomeId: string | number;
  income: CombinedIncome;
  onSuccess: () => void;
  settings?: VerificationSettings;
}) => {
  const allowedDocTypes = getAllowedDocTypes(settings, income?.type);

  const [view, setView] = useState<Views>(Views.Intro);
  const [docType, setDocType] = useState<DocType>(allowedDocTypes[0]);
  const [files, setFiles] = useState<File[]>([]);

  const { data: reVerificationPrice, isLoading } = useQuery<number>({
    queryKey: ["REVERIFICATION_PRICE"],
    queryFn: () =>
      instance
        .get("portal/v1/partners/combined_income_reverification_price")
        .then(({ data }) => data.combined_income_reverification_price_cents)
  });

  const mutation = useMutation({
    mutationKey: [
      "REVERIFY",
      "APPLICATION",
      String(applicationId),
      "VERIFICATION",
      String(verificationId),
      "APPLICANT",
      String(applicant.id)
    ],
    mutationFn: ({ docType, files }: { docType: DocType; files: File[] }) => {
      return Promise.all(
        files.map((file) => {
          return getPresignedUrl(file)
            .then((url) => uploadFileToS3Bucket(url, file))
            .then(({ url }) => url);
        })
      ).then((links) => {
        const ret = {
          income_source_type: docType,
          documents: files.map((file, idx) => ({
            document_type: docType,
            url: links[idx],
            metadata: {
              size: file.size,
              original_filename: normalizeFilename(file.name),
              content_type: file.type
            }
          }))
        };

        return instance.post(
          `portal/v1/applications/${applicationId}/verifications/${verificationId}/income/${incomeId}/reverify?applicant_id=${applicant.id}`,
          ret
        );
      });
    },
    onError: (err) => {
      toast.error(
        getFileUploadErrorMessage(err) ||
          getErrorMessage(err, "Error reverifying income")
      );
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    }
  });

  if (!opened || !income) return null;

  const applicantFullName = getFullName({
    firstName: applicant.first_name,
    middleName: applicant.middle_name,
    lastName: applicant.last_name,
    short: true
  });

  return (
    <>
      <div className="flex-grow  min-h-0">
        <Show if={view === Views.Intro}>
          <IntroView
            onClose={onClose}
            applicantRole={getApplicantRole(applicant.role)}
            applicantFullName={applicantFullName}
            incomeStartDate={income.start_date || "-"}
            incomeEndDate="Present"
            incomeSource={income.type}
            incomeName={getIncomeJobName(income.additional_data)}
            cents={reVerificationPrice || 0}
            isLoading={isLoading}
            title="Re-verify income source with documents"
            description="You can re-verify the income source by uploading an income documents here"
          />
        </Show>

        <Show if={view === Views.DocType}>
          <DocTypeView
            onClose={onClose}
            docType={docType}
            setDocType={setDocType}
            docTypes={allowedDocTypes}
            applicantFullName={applicantFullName}
          />
        </Show>

        <Show if={view === Views.UploadDoc}>
          <DropZoneView
            onClose={onClose}
            companyName={
              income.additional_data && "company_name" in income.additional_data
                ? income.additional_data.company_name
                : undefined
            }
            applicantFullName={applicantFullName}
            files={files}
            setFiles={setFiles}
            docType={docType}
            months={settings?.[income.type]?.[docType]?.timeframe?.value ?? 3}
          />
        </Show>

        <Show if={view === Views.Confirm}>
          <ConfirmView
            onClose={onClose}
            applicantRole={getApplicantRole(applicant.role)}
            applicantFullName={applicantFullName}
            cents={reVerificationPrice || 0}
            isLoading={isLoading}
            incomeSource={income.type}
            incomeName={getIncomeJobName(income.additional_data)}
            documentsCount={files.length}
            docType={docType}
          />
        </Show>
      </div>

      <div className="px-6 py-4 border-t border-alpha-100 gap-2 flex justify-end self-end w-full">
        <Button
          className="w-[125px]"
          onClick={() => {
            if (view === Views.Intro) onBack();
            else setView(JOURNEY[JOURNEY.indexOf(view) - 1]);
          }}
        >
          Back
        </Button>

        <Button
          variant={ButtonVariant.PRIMARY}
          className="w-[125px]"
          disabled={
            view === Views.UploadDoc &&
            (!files.length ||
              (docType === DocType.BenefitsStatement && files.length > 1))
          }
          loading={mutation.isPending || (isLoading && view === Views.Confirm)}
          onClick={async () => {
            if (view === Views.Confirm)
              mutation.mutate({
                docType,
                files
              });
            else setView(JOURNEY[JOURNEY.indexOf(view) + 1]);
          }}
        >
          {view === Views.Confirm ? "Verify" : "Continue"}
        </Button>
      </div>
    </>
  );
};
